/*
 * LayOut.jsx — Shell de navegación Little Blue
 *
 * Mobile  : topbar (marca + usuario) + bottom nav (5 tabs)
 * Desktop : topbar (marca + links + usuario) + sidebar izquierdo + contenido
 *
 * Pasa datos del asesor hacia abajo vía Outlet context para evitar
 * que Dashboard haga una segunda llamada a la misma API.
 *
 * Roles:
 *   customer_type 1 = usuario normal
 *   customer_type 2 = delegado
 *   customer_type 3 = tutor
 */

import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDataContext } from "../contexts/AuthContext";
import { getAgentInfo, logOut } from "../services/api";
import logoAzul from "../images/logoAzul.png";
import s from "../styles/Home.module.css";
import { formatLbOrderCode } from "../utils/orderDisplay";

function LayOut() {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextData } = useDataContext();

  const customer     = contextData?.customer ?? {};
  const customerType = customer.customer_type ?? 1;  // 1 | 2 | 3
  const orderType    = customer.order_type    ?? 1;

  /* ── Tutor: modo "buy" | "track" (elegido en el 1er ingreso). En modo seguimiento no
        tiene pagos personales → se le oculta la solapa Pagos. ── */
  const [tutorMode, setTutorMode] = useState(null);
  useEffect(() => {
    if (!customer.id) return;
    const read = () => {
      try { setTutorMode(localStorage.getItem(`mcb_tutor_mode_${customer.id}`) || null); }
      catch (e) { /* ignore */ }
    };
    read();
    window.addEventListener("mcb-tutor-mode", read);
    return () => window.removeEventListener("mcb-tutor-mode", read);
  }, [customer.id]);
  // El tutor ve "Pagos" (personal) solo si eligió comprar; el resto siempre.
  const showPagos = customerType !== 3 || tutorMode === "buy";

  /* ── Datos del asesor ── */
  const [agent, setAgent] = useState({
    firstName: "", surname: "", phone: "", email: "",
  });

  useEffect(() => {
    if (!customer.lead_id) return;
    getAgentInfo(customer.lead_id)
      .then((d) => setAgent({
        firstName: d.first_name,
        surname:   d.last_name,
        phone:     d.agentPhone,
        email:     d.email,
      }))
      .catch(console.error);
  }, [customer.lead_id]);

  /* ── Helpers ── */
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
  const firstName = cap(customer.first_name || "");
  const roleLabel =
    customerType === 3 ? "Tutor" :
    customerType === 2 ? "Delegado/a" : "Usuario";
  const displayOrderCode = formatLbOrderCode(customer.access_code || customer.username);

  /* ── Items de navegación según rol ── */
  const desktopNavItems = [
    { to: "/mcb",                end: true,  iconClass: "bi bi-house-door-fill", label: "Inicio"   },
    ...(showPagos
      ? [{ to: "/mcb/pagos",                 iconClass: "bi bi-wallet2", label: "Pagos" }]
      : []),
    ...(customerType === 3
      ? [{ to: "/mcb/accountsSummary", iconClass: "bi bi-people-fill", label: "Alumnos" }]
      : []),
    { to: "/mcb/prendas",                    iconClass: "bi bi-palette-fill", label: "Diseño"  },
    { to: "/mcb/talles",                     iconClass: "bi bi-rulers", label: "Talles" },
    ...(orderType !== 2
      ? [{ to: "/mcb/entregas",              iconClass: "bi bi-truck", label: "Entregas" }]
      : []),
    { to: "/mcb/faq",                        iconClass: "bi bi-question-circle-fill", label: "FAQ"      },
    ...(orderType !== 2
      ? [{ to: "/mcb/rifas",                 iconClass: "bi bi-ticket-perforated-fill", label: "Rifas"   }]
      : []),
  ];
  const mobileNavItems = [
    { to: "/mcb", end: true, iconClass: "bi bi-house-door-fill", label: "Inicio" },
    ...(showPagos
      ? [{ to: "/mcb/pagos", iconClass: "bi bi-wallet2", label: "Pagos" }]
      : []),
    ...(customerType === 3
      ? [{ to: "/mcb/accountsSummary", iconClass: "bi bi-people-fill", label: "Alumnos" }]
      : []),
    { to: "/mcb/prendas", iconClass: "bi bi-palette-fill", label: "Diseño" },
    { to: "/mcb/talles", iconClass: "bi bi-rulers", label: "Talles" },
    ...(orderType !== 2
      ? [{ to: "/mcb/entregas", iconClass: "bi bi-truck", label: "Envíos" }]
      : []),
    { to: "/mcb/faq", iconClass: "bi bi-question-circle-fill", label: "FAQ" },
    ...(customerType !== 3 && orderType !== 2
      ? [{ to: "/mcb/rifas", iconClass: "bi bi-ticket-perforated-fill", label: "Rifas" }]
      : []),
  ];

  const handleLogOut = () =>
    logOut().then(() => navigate("/")).catch(console.error);

  const hideMobileBottomNav = location.pathname.includes("/mcb/prendas/designer/");
  // Dentro del área de diseño (selección de prenda + diseñador) ocultamos el panel lateral
  // (Nº pedido / asesor): no aporta nada ahí y come mucho ancho útil de la pantalla.
  const hideSidebar = location.pathname.includes("/mcb/prendas");

  /* ── Outlet context: evita segunda llamada a getAgentInfo en Dashboard ── */
  const outletCtx = { agent, customerType, orderType };

  return (
    <div className={s.root}>

      {/* ════ TOPBAR ════ */}
      <header className={s.topbar}>
        {/* Links de nav — solo desktop */}
        <nav className={s.desktopNav} aria-label="Navegación principal">
          {desktopNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `${s.desktopLink} ${isActive ? s.desktopLinkActive : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Usuario */}
        <div className={s.userChip}>
          <span className={s.avatar} aria-hidden="true">
            {firstName.charAt(0)}
          </span>
          <span className={s.userName}>{firstName}</span>
          <button
            className={s.logoutBtn}
            onClick={handleLogOut}
            aria-label="Cerrar sesión"
          >
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* ════ SHELL (sidebar + main) ════ */}
      <div className={s.shell}>

        {/* ── SIDEBAR (solo desktop; oculto en el área de diseño) ── */}
        {!hideSidebar && (
        <aside className={s.sidebar} aria-label="Panel lateral">

          <img src={logoAzul} alt="Little Blue" className={s.sidebarLogo} />

          {/* Nº pedido */}
          <NavLink to="/switch" className={s.orderBlock}>
            <span className={s.orderLabel}>Nº pedido</span>
            <span className={s.orderCode}>{displayOrderCode}</span>
            <span className={s.orderSwitch}>Tocá para cambiar de pedido →</span>
          </NavLink>

          {/* Saludo + badge */}
          <div className={s.greetingBlock}>
            <div className={s.greeting}>
              <span className={s.greetingLight}>Hola </span>
              {firstName}!
            </div>
            <span className={s.roleBadge}>{roleLabel}</span>
          </div>

          {/* Asesor */}
          <div className={s.advisorBlock}>
            <div className={s.advisorHeading}>MI ASESOR/A</div>
            <div className={s.advisorName}>{agent.firstName} {agent.surname}</div>
            <div className={s.advisorRows}>
              <div className={s.advisorRow}><i className="bi bi-telephone-fill" aria-hidden="true" /> 39600101</div>
            {agent.phone && (
              <div className={s.advisorRow}>
                <i className="bi bi-whatsapp" aria-hidden="true" />{" "}
                <a
                  href={`https://wa.me/${agent.phone}`}
                  className={s.advisorLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {agent.phone}
                </a>
              </div>
            )}
            {agent.email && (
              <div className={s.advisorRow}>
                <i className="bi bi-envelope-fill" aria-hidden="true" /> {agent.email}
              </div>
            )}
            </div>
            <div className={s.advisorHours}>Lun-Vie 9-13 / 14-18 hs</div>
          </div>

          {/* Encuesta */}
          <a
            href="https://forms.gle/8bTDRNQw1tT3VkSy5"
            className={s.surveyLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-chat-left-heart-fill" aria-hidden="true" /> Nos interesa tu opinión
          </a>
        </aside>
        )}

        {/* ── CONTENIDO PRINCIPAL ── */}
        <main className={`${s.main} ${hideMobileBottomNav ? s.mainDesignerMobile : ""}`}>
          <Outlet context={outletCtx} />
        </main>

      </div>

      {/* ════ BOTTOM NAV (solo mobile) ════ */}
      {!hideMobileBottomNav && (
        <nav className={s.bottomNav} aria-label="Navegación inferior">
          {mobileNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `${s.navItem} ${isActive ? s.navItemActive : ""}`
              }
            >
              <span className={s.navIcon} aria-hidden="true">
                <i className={item.iconClass} />
              </span>
              <span className={s.navLabel}>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      )}

    </div>
  );
}

export default LayOut;
